import { useEffect, useMemo, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox, selectableRowClass } from '@/components/ui/range-select';
import { useRangeSelection } from '@/hooks/useRangeSelection';
import { cn } from '@/lib/utils';
import {
  useAdminInvites,
  useCreateManualPayout,
} from '@/hooks/useAdmin';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { OutreacherPicker } from '@/components/money/OutreacherPicker';
import { channelLabel } from '@/components/ui/tag';
import { GLOSSARY, inviteStatusLabel } from '@/lib/labels';
import { formatMoney } from '@/lib/money';
import type { OutreachInvite } from '@/api/types';

interface ManualPayoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LineState {
  checked: boolean;
  amount: string;
}

function owedFor(invite: OutreachInvite): number {
  const locked = Number(invite.lockedValue ?? 0);
  const paid = Number(invite.amountPaid ?? 0);
  const owed = locked - paid;
  return owed > 0 ? owed : 0;
}

export function ManualPayoutDialog({
  open,
  onOpenChange,
}: ManualPayoutDialogProps): JSX.Element {
  const [selectedInviterId, setSelectedInviterId] = useState<string>('');
  const [selectedName, setSelectedName] = useState<string>('');
  const [lineState, setLineState] = useState<Record<number, LineState>>({});
  // Blank means "MANUAL" on the backend; the input shows a plain-words hint.
  const [paidVia, setPaidVia] = useState('');
  const [externalReceiptId, setExternalReceiptId] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [notes, setNotes] = useState('');

  const invites = useAdminInvites(
    selectedInviterId
      ? { inviterId: Number(selectedInviterId), showOwed: 'true', limit: 200 }
      : undefined,
    { enabled: !!selectedInviterId },
  );
  const record = useCreateManualPayout();
  const invalidateMoney = useInvalidateMoney();

  // Reset line item selections when the inviter changes so state from a
  // previously-picked inviter doesn't leak across.
  useEffect(() => {
    setLineState({});
  }, [selectedInviterId]);

  const owedInvites = useMemo(
    () =>
      (invites.data?.data ?? []).filter((inv) => owedFor(inv) > 0),
    [invites.data],
  );

  const total = useMemo(() => {
    let sum = 0;
    for (const inv of owedInvites) {
      const s = lineState[inv.id];
      if (!s?.checked) continue;
      const amt = Number(s.amount);
      if (Number.isFinite(amt) && amt > 0) sum += amt;
    }
    return sum;
  }, [lineState, owedInvites]);
  const totalDisplay = total.toFixed(2);

  /** Tick or untick invites; a newly ticked one starts at its full owed amount. */
  function setInvitesChecked(ids: number[], checked: boolean): void {
    const byId = new Map(owedInvites.map((inv) => [inv.id, inv]));
    setLineState((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        const cur = prev[id];
        const inv = byId.get(id);
        if (!checked) {
          if (cur) next[id] = { ...cur, checked: false };
        } else if (inv) {
          next[id] = { checked: true, amount: cur?.amount ?? owedFor(inv).toFixed(2) };
        }
      }
      return next;
    });
  }

  const range = useRangeSelection({
    visible: owedInvites.map((inv) => inv.id),
    isSelected: (id) => !!lineState[id]?.checked,
    onApply: setInvitesChecked,
  });
  const checkedCount = owedInvites.filter((inv) => lineState[inv.id]?.checked).length;

  function updateAmount(inviteId: number, amount: string): void {
    setLineState((prev) => ({
      ...prev,
      [inviteId]: {
        checked: prev[inviteId]?.checked ?? true,
        amount,
      },
    }));
  }

  function reset(): void {
    setSelectedInviterId('');
    setSelectedName('');
    setLineState({});
    setPaidVia('');
    setExternalReceiptId('');
    setPaidAt('');
    setNotes('');
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const inviterId = Number(selectedInviterId);
    if (!inviterId) {
      toast.error('Pick an outreacher');
      return;
    }
    const lineItems = owedInvites
      .filter((inv) => lineState[inv.id]?.checked)
      .map((inv) => {
        const raw = lineState[inv.id]?.amount ?? '';
        const num = Number(raw);
        return {
          inviteId: inv.id,
          amount: Number.isFinite(num) && num > 0 ? num.toFixed(2) : '',
        };
      })
      .filter((li) => li.amount !== '');
    if (lineItems.length === 0) {
      toast.error('Pick at least one invite and enter an amount above $0');
      return;
    }
    try {
      const result = await record.mutateAsync({
        inviterId,
        lineItems,
        paidVia: paidVia.trim() || 'MANUAL',
        ...(externalReceiptId.trim()
          ? { externalReceiptId: externalReceiptId.trim() }
          : {}),
        ...(paidAt ? { paidAt: new Date(paidAt).toISOString() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      invalidateMoney();
      toast.success(
        `Recorded $${formatMoney(result.totalAmount)} paid${selectedName ? ` to ${selectedName}` : ''}`,
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record off-platform payment</DialogTitle>
          <DialogDescription>
            For money you already paid outside the platform. It's saved as a Paid
            payout and no provider is contacted. Each amount you enter is added to
            what that invite has been paid. Leave out invites you're waiving; write
            those off with Adjust owed amount on Invites.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4" noValidate>
          <div className="grid gap-1">
            <Label htmlFor="mp-outreacher">Outreacher</Label>
            <OutreacherPicker
              id="mp-outreacher"
              value={selectedInviterId}
              onChange={(id, name) => {
                setSelectedInviterId(id);
                setSelectedName(name ?? '');
              }}
              placeholder="Search by name or email"
            />
          </div>

          {selectedInviterId && (
            <div className="grid gap-2">
              <Label>Invites with money owed</Label>
              {invites.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading invites…</p>
              ) : owedInvites.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nothing is owed on this outreacher's invites. Everything is
                  already paid.
                </p>
              ) : (
                <>
                <RangeSelectHint show={checkedCount > 0} />
                <div className="max-h-72 overflow-y-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left">
                          <SelectAllCheckbox
                            label="Select every invite with money owed"
                            state={range.headState}
                            onToggle={range.toggleAll}
                            className="accent-[var(--primary)]"
                          />
                        </th>
                        <th className="p-2 text-left">Creator</th>
                        <th className="p-2 text-right" title={GLOSSARY.owedBeforeAdjustments}>
                          Owed
                        </th>
                        <th className="p-2 text-right">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {owedInvites.map((inv) => {
                        const s = lineState[inv.id];
                        const owedStr = owedFor(inv).toFixed(2);
                        return (
                          <tr
                            key={inv.id}
                            className={cn(
                              'border-t transition-colors',
                              s?.checked ? 'bg-brand/10' : 'hover:bg-accent/40',
                              selectableRowClass(),
                            )}
                            {...range.rowProps(inv.id)}
                          >
                            <td className="p-2 align-middle">
                              <RowCheckbox
                                aria-label={`Select ${inv.targetIdentifier}`}
                                className="accent-[var(--primary)]"
                                {...range.checkboxProps(inv.id)}
                              />
                            </td>
                            <td className="p-2 text-xs">
                              <div className="font-medium">{inv.targetIdentifier}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {channelLabel(inv.channel)} · {inviteStatusLabel(inv.status)}
                              </div>
                            </td>
                            <td className="p-2 text-right tabular-nums">${formatMoney(owedStr)}</td>
                            <td className="p-2 text-right">
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={s?.amount ?? owedStr}
                                disabled={!s?.checked}
                                onChange={(e) =>
                                  updateAmount(inv.id, e.target.value)
                                }
                                className="ml-auto h-7 w-24 text-right"
                                aria-label={`Amount paid for ${inv.targetIdentifier}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label htmlFor="mp-paidVia">Paid via</Label>
              <Input
                id="mp-paidVia"
                value={paidVia}
                onChange={(e) => setPaidVia(e.target.value)}
                placeholder="e.g. PayPal, bank transfer"
                maxLength={30}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="mp-paidAt">Paid at</Label>
              <Input
                id="mp-paidAt"
                type="datetime-local"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </div>
            <div className="grid gap-1 sm:col-span-2">
              <Label htmlFor="mp-receipt">Receipt or transaction ID</Label>
              <Input
                id="mp-receipt"
                value={externalReceiptId}
                onChange={(e) => setExternalReceiptId(e.target.value)}
                placeholder="e.g. PayPal transaction ID, clipping payment ID"
              />
            </div>
            <div className="grid gap-1 sm:col-span-2">
              <Label htmlFor="mp-notes">Notes</Label>
              <Input
                id="mp-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What was actually paid, where, and why"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border bg-muted/40 p-3 text-sm">
            <span className="text-muted-foreground">Total</span>
            <span className="font-medium tabular-nums">${formatMoney(totalDisplay)}</span>
          </div>

          <div className="flex gap-2 rounded-md border border-yellow-500/40 bg-yellow-50/50 p-2 text-xs text-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-200">
            <CircleAlert className="h-4 w-4 shrink-0" />
            <span>
              Saved as Paid straight away and nothing is sent. For invites you're
              waiving, open Invites and use Adjust owed amount to write off what's
              left, with a reason.
            </span>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={record.isPending || total <= 0}>
              {record.isPending ? 'Recording…' : `Record $${formatMoney(totalDisplay)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
